import { motion } from "framer-motion";
import { Shield, Target, Users, Heart, BookOpen, Compass } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

const values = [
  { icon: Target, title: "Clarity", desc: "We turn complex processes into clear, understandable next steps." },
  { icon: Shield, title: "Integrity", desc: "We tell the truth about what's possible — even when it's the harder answer." },
  { icon: Users, title: "Service", desc: "We serve veterans, families, and individuals with dedication and empathy." },
  { icon: Heart, title: "Care", desc: "Every client is treated as a person, not a case number." },
  { icon: BookOpen, title: "Education", desc: "We equip you with knowledge — so you can advocate for yourself long-term." },
  { icon: Compass, title: "Strategy", desc: "Every recommendation is guided by evidence and a clear plan." },
];

const AboutPage = () => (
  <div className="pt-32 pb-24 bg-white">
    <div className="max-w-5xl mx-auto px-6 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
        <span className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-3 block">
          About Noble Strategies
        </span>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B2343] leading-tight mb-8">
          Built by veterans.<br />
          <span className="text-[#C89B3C]">For everyone who serves — and their families.</span>
        </h1>
      </motion.div>

      <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-6 mb-16">
        <p>
          Noble Strategies Consulting is a veteran-owned firm dedicated to helping people navigate
          some of life's most consequential decisions — from securing the disability benefits
          they've earned, to protecting the people they love, to planning the next chapter of their
          careers.
        </p>
        <p>
          We started this company because we saw too many veterans, families, and individuals
          trying to make major decisions with too little support. Complex paperwork. Confusing
          eligibility rules. Sales pitches disguised as advice. Our answer is a different kind of
          consultancy: one that combines strategy, evidence, and honest guidance so our clients
          can move forward with clarity and confidence.
        </p>
      </div>

      <SectionHeading
        eyebrow="Our Mission"
        title="Strategy. Evidence. Results."
        subtitle="We exist to make the systems that shape people's lives — VA, insurance, and education — more understandable, more navigable, and more human."
      />

      <SectionHeading eyebrow="What We Stand For" title="Our values." align="center" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
        {values.map((v, idx) => {
          const Icon = v.icon;
          return (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className="p-6 bg-slate-50 border-l-4 border-[#C89B3C]"
            >
              <Icon className="text-[#C89B3C] mb-3" size={26} />
              <h3 className="font-serif text-lg font-bold text-slate-900 mb-2">{v.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="bg-[#0B2343] text-white p-10 md:p-14">
        <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
        <h2 className="font-serif text-2xl md:text-3xl font-bold mb-4">Professional Standards</h2>
        <p className="text-slate-300 leading-relaxed">
          Noble Strategies Consulting is not a law firm, healthcare provider, or accredited VA
          representative unless separately stated. We provide consulting, education, and
          advocacy — with clear expectations set from the very first conversation.
        </p>
      </div>
    </div>
  </div>
);

export default AboutPage;
